import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import Layout from './components/Layout';
import Users from './components/Users';
import Companies from './components/Companies';
import Projects from './components/Projects';
import ProjectReports from './components/ProjectReports';
import Retail from './components/Retail';
import CompanyProjects from './components/CompanyProjects';
import CompanyUsers from './components/CompanyUsers';
import CompanyRetailers from './components/CompanyRetailers';
import ProjectDetails from './components/ProjectDetails';
import ReleaseReport from './components/ReleaseReport';
import Profile from './components/Profile';
import DashboardMain from './components/DashboardMain';
import Email from './components/Email';
import EmailLogs from './components/EmailLogs';
import EmailTemplates from './components/EmailTemplates';
import Applications from './components/Applications';
import PortfolioProjects from './components/PortfolioProjects';
import Deliveries from './components/Deliveries';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import LogoutOnLockout from './contexts/LogoutOnLockout';
import { UserProvider } from './contexts/UserContext';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import './font-override.css';
import './components/GlobalResponsive.css';

function App() {
  useEffect(() => {
    // Inject font styles directly to ensure they're applied
    const style = document.createElement('style');
    style.textContent = `
      *,
      *::before,
      *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
      html *,
      html *::before,
      html *::after,
      body *,
      body *::before,
      body *::after,
      #root *,
      #root *::before,
      #root *::after,
      .App *,
      .App *::before,
      .App *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    // Cleanup function
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return (
    <UserProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public routes - redirect to companies if user is already logged in */}
            <Route path="/" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/logout-on-lockout" element={
              <PublicRoute>
                <LogoutOnLockout />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } />
            
            {/* Dashboard - superAdmin only */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<DashboardMain />} />
            </Route>
            
            {/* User Management - Only accessible by superAdmin and companyAdmin */}
            <Route path="/users" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin', 'companyAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<Users />} />
            </Route>
            
            <Route path="/companies" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Companies />} />
            </Route>
            <Route path="/company/:companyId/projects" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<CompanyProjects />} />
            </Route>
            <Route path="/company/:companyId/users" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<CompanyUsers />} />
            </Route>
            <Route path="/company/:companyId/retailers" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<CompanyRetailers />} />
            </Route>
            <Route path="/company/:companyId/project/:projectId" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<ProjectDetails />} />
              <Route path="release-report" element={<ReleaseReport />} />
            </Route>
            <Route path="/projects" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Projects />} />
            </Route>
            <Route path="/project-reports" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<ProjectReports />} />
            </Route>
            <Route path="/retail" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Retail />} />
            </Route>
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Profile />} />
            </Route>

            {/* Email - superAdmin only */}
            <Route path="/email" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<Email />} />
            </Route>
            
            <Route path="/email-templates" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<EmailTemplates />} />
            </Route>
            
            <Route path="/email-logs" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<EmailLogs />} />
            </Route>
            
            {/* Applications - superAdmin only */}
            <Route path="/applications" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<Applications />} />
            </Route>
            
            {/* Portfolio Projects - superAdmin only */}
            <Route path="/portfolio-projects" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<PortfolioProjects />} />
            </Route>

            {/* Logistics deliveries - superAdmin only */}
            <Route path="/deliveries" element={
              <ProtectedRoute>
                <RoleBasedRoute allowedRoles={['superAdmin']}>
                  <Layout />
                </RoleBasedRoute>
              </ProtectedRoute>
            }>
              <Route index element={<Deliveries />} />
            </Route>
            
            {/* Catch all route - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </UserProvider>
  );
}

export default App;

